// Where a logged-out visitor was headed when the auth guard bounced them to
// /login — e.g. /map?latitude=…&mac=… from an alert mail or a push
// notification — so login can take them back there instead of to "/".
//
// Same storage shape as pendingClaim.ts: per-tab sessionStorage (survives the
// /select-organization hard reload), timestamped so an abandoned redirect does
// not fire on some later login in the same tab, and every access guarded so
// blocked storage degrades to the old behaviour (land on "/").

const KEY = "loginReturnPath";

const MAX_AGE_MS = 60 * 60 * 1000;

// Longer than any real app link (the alert mail's /map link is ~150 chars);
// anything past this is not ours.
const MAX_LENGTH = 2048;

// Public-paths whitelist for the auth guard (everything else is protected).
// Also the pages login must never "return" to: they are the auth flow itself
// (a loop) or need no login at all.
export const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/signUp",
  "/forgotlogin",
  "/airmap",
  "/addAirplate",
  "/addOperatorId",
  "/finishSignUp",
  "/checkout",
  "/success",
  "/subscribe",
  "/select-organization",
];

// Placeholder origin: only used to prove the path cannot leave the app.
const BASE = "https://app.invalid";

/**
 * `raw` if it is an internal, protected app path (pathname + search), else
 * null. Open-redirect guard: anything that could resolve to another origin is
 * refused, not repaired.
 */
export function safeReturnPath(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  if (raw.length === 0 || raw.length > MAX_LENGTH) return null;
  // Exactly one leading slash: "//host" and "/\host" are protocol-relative.
  if (raw[0] !== "/" || raw[1] === "/") return null;
  // Browsers read "\" as "/", and the URL parser strips tab/newline, so
  // "/\t/evil.com" would become "//evil.com". Refuse both outright.
  // eslint-disable-next-line no-control-regex -- matching control chars is the point
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, BASE);
  } catch {
    return null;
  }
  if (url.origin !== BASE) return null;
  // React Router matches paths case-insensitively, so "/LOGIN" is /login.
  const path = url.pathname.toLowerCase();
  const isPublic = PUBLIC_PATH_PREFIXES.some((p) => {
    const prefix = p.toLowerCase();
    return path === prefix || path.startsWith(prefix + "/");
  });
  return isPublic ? null : raw;
}

function defaultStorage(): Storage | undefined {
  return typeof sessionStorage === "undefined" ? undefined : sessionStorage;
}

/** Remember `path` for after login; unsafe paths are dropped silently. */
export function saveReturnPath(path: string, storage = defaultStorage()): void {
  const safe = safeReturnPath(path);
  if (!safe) return;
  try {
    storage?.setItem(KEY, JSON.stringify({ path: safe, at: Date.now() }));
  } catch {
    // Storage unavailable: login lands on "/" as before.
  }
}

export function readReturnPath(
  storage = defaultStorage(),
  now = Date.now(),
): string | null {
  try {
    const raw = storage?.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof parsed.at !== "number" || now - parsed.at > MAX_AGE_MS) return null;
    // Re-checked on read: storage is writable by anything on this origin.
    return safeReturnPath(parsed.path);
  } catch {
    return null;
  }
}

export function clearReturnPath(storage = defaultStorage()): void {
  try {
    storage?.removeItem(KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
